import { Clock } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export function TimeAttendanceSection({ formData, handleChange }) {
  return (
              <div className="form-section border-blue-500/30 bg-blue-500/5">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-blue-400" />
                  <h2 className="form-section-title text-blue-400 !mb-0">Time & Attendance</h2>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-4 rounded-lg bg-secondary/30">
                    <Checkbox
                      id="is_hourly_paid"
                      checked={formData.is_hourly_paid}
                      onCheckedChange={(v) => {
                        handleChange('is_hourly_paid', v);
                        // Clear pay cycle if unchecked; reset shift_type default
                        if (!v) {
                          handleChange('hourly_pay_cycle', '');
                        } else if (!formData.shift_type) {
                          handleChange('shift_type', 'Day Shift');
                        }
                      }}
                      data-testid="checkbox-hourly-paid"
                    />
                    <div className="space-y-1">
                      <Label htmlFor="is_hourly_paid" className="text-base font-medium cursor-pointer">
                        Hourly Paid (Requires Clock-In/Out)
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Enable this for employees who need to clock in and out using the shop floor kiosk. 
                        Only hourly-paid employees will appear on the kiosk and be tracked for attendance.
                      </p>
                    </div>
                  </div>
            
                  {/* Pay Cycle + Shift Type - only visible if hourly paid */}
                  {formData.is_hourly_paid && (
                    <div className="p-4 rounded-lg bg-secondary/30 space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="hourly_pay_cycle" className="text-base font-medium">
                          Pay Cycle *
                        </Label>
                        <p className="text-sm text-muted-foreground mb-2">
                          Select how this employee is paid for time tracking and reporting purposes.
                        </p>
                        <Select
                          value={formData.hourly_pay_cycle}
                          onValueChange={(v) => handleChange('hourly_pay_cycle', v)}
                        >
                          <SelectTrigger className="w-64" data-testid="select-pay-cycle">
                            <SelectValue placeholder="Select pay cycle" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Fortnightly">
                              <div className="flex flex-col">
                                <span>Fortnightly</span>
                                <span className="text-xs text-muted-foreground">14-day cycle starting 22 April 2026</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="Monthly">
                              <div className="flex flex-col">
                                <span>Monthly</span>
                                <span className="text-xs text-muted-foreground">24th to 23rd of each month</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2 pt-2 border-t border-border/40">
                        <Label htmlFor="shift_type" className="text-base font-medium">
                          Default Shift Type (advisory)
                        </Label>
                        <p className="text-sm text-muted-foreground mb-2">
                          Actual shift is <strong>auto-detected from the clock-in time</strong> (morning = Day, evening = Night),
                          so employees can alternate week-to-week without changing this field.
                          Day Shift: 07:00–17:30 Mon–Thu. Night Shift: 20:00–06:00 next morning.
                          Friday hours are always overtime.
                        </p>
                        <Select
                          value={formData.shift_type || 'Day Shift'}
                          onValueChange={(v) => handleChange('shift_type', v)}
                        >
                          <SelectTrigger className="w-64" data-testid="select-shift-type">
                            <SelectValue placeholder="Select shift type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Day Shift">Day Shift (default)</SelectItem>
                            <SelectItem value="Night Shift">Night Shift (default)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
  );
}

export function LeaveAccrualSection({ formData, handleChange }) {
  return (
              <div className="form-section border-purple-500/30 bg-purple-500/5">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-purple-400" />
                  <h2 className="form-section-title text-purple-400 !mb-0">Leave Accrual Anchors</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Set these dates manually to enable automatic leave accrual. <strong>Annual Leave</strong> accrues at <strong>1.25 days per month</strong> (15 days / year).
                  <strong> Sick Leave</strong> resets to <strong>30 days every 3 years</strong> from the cycle start. Leave blank to skip auto-accrual for this employee.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="annual_leave_accrual_start">Annual Leave Accrual Start</Label>
                    <Input
                      id="annual_leave_accrual_start"
                      type="date"
                      value={formData.annual_leave_accrual_start || ''}
                      onChange={(e) => handleChange('annual_leave_accrual_start', e.target.value)}
                      data-testid="input-annual-accrual-start"
                    />
                    <p className="text-xs text-muted-foreground">1.25 days added each month from this date.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sick_cycle_start">Sick Cycle Start</Label>
                    <Input
                      id="sick_cycle_start"
                      type="date"
                      value={formData.sick_cycle_start || ''}
                      onChange={(e) => handleChange('sick_cycle_start', e.target.value)}
                      data-testid="input-sick-cycle-start"
                    />
                    <p className="text-xs text-muted-foreground">Balance resets to 30 days every 3 years.</p>
                  </div>
                </div>
              </div>
  );
}
